import * as React from "react";
import { Drawer, DrawerContent } from "../../../components/ui/drawer";
import { RestaurantDrawerProps } from "../../../types/restaurant";
import { TimeSelectWheel } from "../../../components/TimeSelectWheel";
import { DateSelectWheel } from "../../../components/DateSelectWheel";
import { PeopleSelectWheel } from "../../../components/PeopleSelectWheel";
import { ArrowRight } from "lucide-react";
import { useTableBooking } from "../../../hooks/useTableBooking";
import toast from "react-hot-toast";
import { Button } from "../../../components/ui/button";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

/**
 * RestaurantDrawer Component
 *
 * A slide-up drawer panel that displays detailed information about a selected restaurant.
 * Uses shadcn Drawer component for smooth animations and accessibility.
 *
 * Features:
 * - Restaurant image display
 * - Name, rating, and address information
 * - Status badge (Active/DeActive)
 * - Store type and timings
 * - Two action buttons: "View Store" and "Book Table"
 */
export const RestaurantDrawer: React.FC<RestaurantDrawerProps> = ({
  restaurant,
  isOpen,
  onClose,
  onViewStore,
  onBookTable,
  mode = "booking",
}) => {
  const [numberOfPeople, setNumberOfPeople] = React.useState(1);
  const [date, setDate] = React.useState("");
  const [time, setTime] = React.useState("");
  const [phoneNumber, setPhoneNumber] = React.useState("");
  const [isDateDrawerOpen, setIsDateDrawerOpen] = React.useState(false);
  const [isTimeDrawerOpen, setIsTimeDrawerOpen] = React.useState(false);
  const [isPeopleDrawerOpen, setIsPeopleDrawerOpen] = React.useState(false);

  const { mutate: createBooking, isPending } = useTableBooking();
  const navigate = useNavigate();

  if (!restaurant) return null;

  const isTakeaway = mode === "takeaway";

  const handleViewStore = () => {
    onViewStore(restaurant.dbID);
  };

  const handlePlaceOrder = () => {
    // Validate inputs for takeaway
    if (!date) {
      toast.error("Please select a date");
      return;
    }

    if (!time) {
      toast.error("Please select a time");
      return;
    }

    // Navigate to restaurant page with date and time
    navigate(
      `/restaurent?storeID=${restaurant.dbID}&storeType=${
        restaurant.storeType
      }&date=${format(new Date(date), "yyyy-MM-dd")}&time=${time}`
    );
    onClose();
  };

  const handleBookTable = () => {
    // Validate inputs
    if (!numberOfPeople || numberOfPeople < 1) {
      toast.error("Please select number of people");
      return;
    }

    if (!date) {
      toast.error("Please select a date");
      return;
    }

    if (!time) {
      toast.error("Please select a time");
      return;
    }

    if (!phoneNumber || phoneNumber.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }

    // Create booking
    createBooking(
      {
        restaurantId: restaurant.dbID,
        restaurantName: restaurant.name,
        numberOfPeople: numberOfPeople,
        bookingDate: format(new Date(date), "yyyy-MM-dd"),
        bookingTime: time,
        phoneNumber: phoneNumber,
      },
      {
        onSuccess: (bookingId) => {
          toast.success("Table reserved successfully!");
          console.log("Booking created with ID:", bookingId);
          // Reset form
          setNumberOfPeople(1);
          setPhoneNumber("");
          onClose();
        },
        onError: (error) => {
          toast.error(error.message || "Failed to reserve table");
          console.error("Booking error:", error);
        },
      }
    );
  };

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent
        className="max-h-[90vh] bg-[#1a1a1a] border-t border-gray-800"
        aria-label="Restaurant details"
      >
        <div className="overflow-y-auto pt-3">
          <div className="relative">
            {/* Restaurant Image */}
            {restaurant.image && (
              <div className="w-full px-4 mb-4">
                <div className="w-full h-36 rounded-2xl overflow-hidden">
                  <img
                    src={restaurant.image}
                    alt={`${restaurant.name} restaurant`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}

            {restaurant.restRating && (
              <div
                className="absolute top-2 right-6 flex items-center gap-1 bg-black px-3 py-1.5 rounded-full"
                aria-label={`Rating: ${restaurant.restRating} stars out of 5`}
              >
                <span className="text-yellow-400 text-sm" aria-hidden="true">
                  ★
                </span>
                <span className="font-semibold text-white">
                  {restaurant.restRating}
                </span>
                {restaurant.restRating && (
                  <span className="text-gray-400 text-xs ml-1">(209)</span>
                )}
              </div>
            )}
            {/* View Store Button */}
            <button
              onClick={handleViewStore}
              className="absolute w-full bottom-0 py-3 justify-center bg-black/40 underline text-white font-semibold  rounded-xl transition-colors flex items-center"
              aria-label={`View ${restaurant.name} store details`}
            >
              View Store <ArrowRight className="h-4" />
            </button>
          </div>

          {/* Restaurant Info */}
          <div className="px-6 pb-5">
            {/* Restaurant Name and Rating */}
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-4 ">
                <h2 className="text-2xl font-bold text-white">
                  {restaurant.name}
                </h2>
              </div>
            </div>

            {/* Store Type Tags */}
            <div className="flex flex-wrap gap-x-2 mb-3">
              {restaurant.storeType && (
                <span className="text-gray-400 text-sm">
                  •{restaurant.storeType}
                </span>
              )}
              {restaurant.pureVeg && (
                <span className="text-gray-400 text-sm">•BreakFast</span>
              )}
              <span className="text-gray-400 text-sm">•Chinese</span>
              <span className="text-gray-400 text-sm">•Desserts</span>
              <span className="text-gray-400 text-sm">•Healthy</span>
              <span className="text-gray-400 text-sm">•Indian</span>
              <span className="text-gray-400 text-sm">•Pizza</span>
              <span className="text-gray-400 text-sm">•Shakes</span>
              <span className="text-gray-400 text-sm">•Shawarma</span>
              <span className="text-gray-400 text-sm">•Snacks</span>
            </div>

            {/* Address Section */}
            {restaurant.address && (
              <div className="mb-4">
                <p className="text-gray-300 text-sm leading-relaxed">
                  {restaurant.address}
                  {restaurant.area && `, ${restaurant.area}`}
                  {restaurant.city && `, ${restaurant.city}`}
                  {restaurant.pincode && ` - ${restaurant.pincode}`}
                </p>
              </div>
            )}

            {/* Form - Different for Takeaway vs Booking */}
            <div className="mb-4 space-y-3">
              {isTakeaway && <p>Preorder Now</p>}
              {/* Date and Time Row - Common for both */}
              <div className="grid grid-cols-2 gap-4">
                {/* Date */}
                <div>
                  <label
                    htmlFor="date"
                    className="block text-gray-400 text-sm mb-2"
                  >
                    Date
                  </label>
                  <input
                    type="text"
                    readOnly
                    placeholder="Select Date"
                    value={date}
                    onClick={() => setIsDateDrawerOpen(true)}
                    className="w-full bg-black text-white px-4 py-3 rounded-xl border border-gray-800 focus:border-[#6366f1] focus:outline-none transition-colors placeholder:text-gray-600 cursor-pointer"
                  />
                </div>
                {/* Time */}
                <div>
                  <label
                    htmlFor="time"
                    className="block text-gray-400 text-sm mb-2"
                  >
                    Time
                  </label>
                  <input
                    type="text"
                    readOnly
                    placeholder="Select Time"
                    value={time}
                    onClick={() => setIsTimeDrawerOpen(true)}
                    className="w-full bg-black text-white px-4 py-3 rounded-xl border border-gray-800 focus:border-[#6366f1] focus:outline-none transition-colors placeholder:text-gray-600 cursor-pointer"
                  />
                </div>
              </div>

              {/* Additional fields only for Table Booking */}
              {!isTakeaway && (
                <>
                  {/* Number of People and Phone Number Row */}
                  <div className="grid grid-cols-[1fr_2fr] gap-4">
                    {/* Number of People */}
                    <div>
                      <label
                        htmlFor="numberOfPeople"
                        className="block text-gray-400 text-sm mb-2"
                      >
                        Attendees
                      </label>
                      <input
                        type="text"
                        readOnly
                        placeholder="Select People"
                        value={
                          numberOfPeople
                            ? `${numberOfPeople} ${
                                numberOfPeople === 1 ? "person" : "people"
                              }`
                            : ""
                        }
                        onClick={() => setIsPeopleDrawerOpen(true)}
                        className="w-full bg-black text-white px-4 py-3 rounded-xl border border-gray-800 focus:border-[#6366f1] focus:outline-none transition-colors placeholder:text-gray-600 cursor-pointer"
                        aria-label="Number of people for reservation"
                      />
                    </div>
                    {/* Phone Number */}
                    <div>
                      <label
                        htmlFor="phoneNumber"
                        className="block text-gray-400 text-sm mb-2"
                      >
                        Phone Number
                      </label>
                      <input
                        id="phoneNumber"
                        type="tel"
                        placeholder="Phone Number"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="w-full bg-black text-white px-4 py-3 rounded-xl border border-gray-800 focus:border-[#6366f1] focus:outline-none transition-colors placeholder:text-gray-600"
                        aria-label="Phone number for reservation"
                      />
                    </div>
                  </div>

                  {/* Note about larger groups */}
                  <div className="bg-[#2a2a2a] border border-gray-800 rounded-lg p-3 mb-3">
                    <p className="text-gray-400 text-[10px] leading-relaxed">
                      <span className="text-gray-300 font-medium">Note:</span>{" "}
                      For groups larger than 6 people, please book one more
                      table or contact the restaurant directly.
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Action Button - Different for Takeaway vs Booking */}
            {isTakeaway ? (
              <Button
                onClick={handlePlaceOrder}
                className="w-full bg-[#6366f1] hover:bg-[#5558e3] text-white font-semibold py-4 rounded-xl transition-colors"
                aria-label={`Place order at ${restaurant.name}`}
              >
                Place Order
              </Button>
            ) : (
              <Button
                onClick={handleBookTable}
                disabled={isPending}
                className="w-full bg-[#6366f1] hover:bg-[#5558e3] disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-xl transition-colors"
                aria-label={`Reserve table at ${restaurant.name}`}
              >
                {isPending ? "Reserving..." : "Reserve Table"}
              </Button>
            )}
          </div>
        </div>
      </DrawerContent>

      {/* Date Picker Drawer */}
      <Drawer open={isDateDrawerOpen} onOpenChange={setIsDateDrawerOpen}>
        <DrawerContent className="bg-[#1a1a1a] border-t border-gray-800 min-h-[40vh]">
          <div className="pl-4 ">
            <h3 className="text-xl font-bold text-white mb-4">Select Date</h3>
            <div className="flex justify-center ">
              <DateSelectWheel onChange={setDate} />
            </div>
            {/* <button className="w-full bg- text-white font-semibold py-4 rounded-xl transition-colors mt-6 !mr-4">
              Confirm
            </button> */}
            <div className="pr-4 pb-4">
              <Button
                onClick={() => setIsDateDrawerOpen(false)}
                className="w-full  bg-[#6366f1] hover:bg-[#5558e3]  text-white font-semibold py-4 rounded-xl transition-colors mt-6 "
              >
                Confirm
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Time Picker Drawer */}
      <Drawer open={isTimeDrawerOpen} onOpenChange={setIsTimeDrawerOpen}>
        <DrawerContent className="bg-[#1a1a1a] border-t border-gray-800">
          <div className="p-6">
            <h3 className="text-xl font-bold text-white mb-4">Select Time</h3>
            <div className="flex justify-center">
              <TimeSelectWheel onChange={setTime} />
            </div>
            <Button
              onClick={() => setIsTimeDrawerOpen(false)}
              className="w-full bg-[#6366f1] hover:bg-[#5558e3]   text-white font-semibold py-4 rounded-xl transition-colors mt-6 "
            >
              Confirm
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* People Picker Drawer */}
      <Drawer open={isPeopleDrawerOpen} onOpenChange={setIsPeopleDrawerOpen}>
        <DrawerContent className="bg-[#1a1a1a] border-t border-gray-800 min-h-[40vh]">
          <div className="pl-4 pt-4">
            <h3 className="text-xl font-bold text-white mb-4">
              Select Number of People
            </h3>
            <div className="flex justify-center">
              <PeopleSelectWheel onChange={setNumberOfPeople} />
            </div>
            <div className="pr-4 pb-4">
              <Button
                onClick={() => setIsPeopleDrawerOpen(false)}
                className="w-full bg-[#6366f1] hover:bg-[#5558e3] text-white font-semibold py-4 rounded-xl transition-colors mt-6"
              >
                Confirm
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </Drawer>
  );
};
